import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard,
  Shield,
  Briefcase,
  Wrench,
  UserCircle,
  ArrowRight,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { GlassCard, LanguageIconDropdown, ThemeIconDropdown } from "./ui";
import type { UserRole } from "../types";
import { getBranding } from "../config/branding";
import { AuthBrandLogo } from "./auth/AuthBrandLogo";

interface LandingViewProps {
  onSelectRole: (role: UserRole) => void;
}

interface RoleCardConfig {
  role: UserRole;
  icon: LucideIcon;
  titleKey: string;
  descKey: string;
  iconWrapClass: string;
  accentClass: string;
}

const ROLE_CARDS: RoleCardConfig[] = [
  {
    role: "admin",
    icon: Shield,
    titleKey: "landing.admin",
    descKey: "landing.adminDesc",
    iconWrapClass: "bg-blue-100 dark:bg-blue-600/20 text-blue-600 dark:text-blue-400",
    accentClass: "text-blue-600 dark:text-blue-400",
  },
  {
    role: "partner",
    icon: Briefcase,
    titleKey: "landing.partner",
    descKey: "landing.partnerDesc",
    iconWrapClass: "bg-purple-100 dark:bg-purple-600/20 text-purple-600 dark:text-purple-400",
    accentClass: "text-purple-600 dark:text-purple-400",
  },
  {
    role: "installer",
    icon: Wrench,
    titleKey: "landing.installer",
    descKey: "landing.installerDesc",
    iconWrapClass: "bg-blue-100 dark:bg-blue-600/20 text-blue-600 dark:text-blue-400",
    accentClass: "text-blue-600 dark:text-blue-400",
  },
  {
    role: "commercial",
    icon: UserCircle,
    titleKey: "landing.commercial",
    descKey: "landing.commercialDesc",
    iconWrapClass: "bg-blue-100 dark:bg-blue-600/20 text-blue-600 dark:text-blue-400",
    accentClass: "text-blue-600 dark:text-blue-400",
  },
];

function RoleCard({
  config,
  onSelect,
}: {
  config: RoleCardConfig;
  onSelect: (role: UserRole) => void;
}) {
  const { t } = useTranslation();
  const Icon = config.icon;

  return (
    <GlassCard role="button" onClick={() => onSelect(config.role)} className="group">
      <div className="flex flex-col items-center text-center gap-3.5 py-7 px-5 sm:py-8 sm:px-6">
        <div
          className={`rounded-2xl p-3.5 transition-transform duration-300 group-hover:scale-105 ${config.iconWrapClass}`}
        >
          <Icon className="w-10 h-10" strokeWidth={1.75} />
        </div>
        <div className="space-y-1.5">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white tracking-tight">
            {t(config.titleKey)}
          </h2>
          <p className="text-sm text-gray-600 dark:text-slate-300 leading-snug line-clamp-2 px-1">
            {t(config.descKey)}
          </p>
        </div>
        <span
          className={`inline-flex items-center gap-1 text-xs font-medium opacity-0 transition-opacity group-hover:opacity-100 ${config.accentClass}`}
        >
          {t("landing.connect")} <ArrowRight className="w-3.5 h-3.5" />
        </span>
      </div>
    </GlassCard>
  );
}

export const LandingView = ({ onSelectRole }: LandingViewProps) => {
  const { t } = useTranslation();
  const branding = getBranding();

  return (
    <div className="h-[100dvh] overflow-hidden flex flex-col items-center justify-center px-4 py-6 sm:px-6 relative">
      <div className="absolute top-4 right-4 z-20 flex items-center gap-1">
        <LanguageIconDropdown />
        <ThemeIconDropdown />
      </div>

      <div className="w-full max-w-md sm:max-w-2xl lg:max-w-5xl flex flex-col items-center">
        <header className="text-center mb-8 sm:mb-10 shrink-0 space-y-3">
          {branding.logoSrc && branding.logoDisplay === "full" ? (
            <div className="flex justify-center max-h-20 overflow-hidden">
              <AuthBrandLogo branding={branding} size="hero" />
            </div>
          ) : (
            <div className="inline-flex p-3 bg-white dark:bg-slate-800 border border-gray-200 dark:border-white/10 rounded-2xl shadow-lg dark:shadow-xl">
              <LayoutDashboard
                className="w-9 h-9 text-blue-600 dark:text-blue-400"
                aria-label={branding.logoAlt}
              />
            </div>
          )}
          {!(branding.logoSrc && branding.logoDisplay === "full") && (
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white tracking-tight">
              {branding.brandName}
            </h1>
          )}
          <p className="text-base text-gray-600 dark:text-slate-300 font-light max-w-lg mx-auto">
            {branding.landingSubtitle ?? t("landing.subtitle")}
          </p>
        </header>

        <div className="w-full grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5 shrink-0">
          {ROLE_CARDS.map((config) => (
            <RoleCard key={config.role} config={config} onSelect={onSelectRole} />
          ))}
        </div>

        <footer className="mt-8 sm:mt-10 shrink-0 text-gray-500 dark:text-slate-400 text-xs text-center">
          <p className="font-medium mb-1.5">v1.0 • {branding.brandName}</p>
          <nav className="flex flex-wrap justify-center gap-x-4 gap-y-1">
            <Link to="/mentions-legales" className="hover:text-blue-600 dark:hover:text-blue-400 underline">
              {t("legal.mentions")}
            </Link>
            <Link to="/cgu" className="hover:text-blue-600 dark:hover:text-blue-400 underline">
              {t("legal.cgu")}
            </Link>
            <Link to="/confidentialite" className="hover:text-blue-600 dark:hover:text-blue-400 underline">
              {t("legal.confidentiality")}
            </Link>
          </nav>
        </footer>
      </div>
    </div>
  );
};
